import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { useAuth } from "../auth/AuthContext";
import TournamentBottomNav from "../components/TournamentBottomNav";
import {
  api,
  ApiError,
  type AdminTeamMember,
  type RefereeMatchRow,
  type RefereesOverview,
} from "../lib/api";
import { compactDateEs } from "../lib/dateFormat";
import {
  blockAssignments,
  courtAssignments,
  dayCourts,
  hourBlocks,
  initialsOf,
  matchChipColor,
  matchChipLabel,
  sortRefereesAZ,
  temporaryPassword,
  type DayPeriod,
} from "../lib/referees";
import { routes } from "../routes";
import "./referees/referees.css";

// «Árbitros» del organizador (maqueta aprobada Arbitros / Arbitros-cel,
// Eduardo 2026-10-16): árbitros del torneo, ¿quién publica?, asignaciones del
// día y el aviso a cada árbitro con su lista.

function errorText(err: unknown, fallback: string): string {
  return err instanceof ApiError ? err.message : fallback;
}

function dayTabLabel(date: string, isToday: boolean): string {
  const compact = compactDateEs(date);
  if (!compact) return date;
  const dayNumber = compact.day.split(" ")[0];
  return `${compact.weekday} ${dayNumber}${isToday ? " · Hoy" : ""}`;
}

function todayIso(): string {
  const now = new Date();
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const dd = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${mm}-${dd}`;
}

interface InviteForm {
  full_name: string;
  phone: string;
  email: string;
  password: string;
}

export default function AdminTournamentRefereesPage() {
  const { id } = useParams<{ id: string }>();
  const tournamentId = Number(id);
  const { token, user } = useAuth();
  const [data, setData] = useState<RefereesOverview | null>(null);
  const [day, setDay] = useState<string | null>(null);
  const [division, setDivision] = useState<number | "all">("all");
  const [onlyReferee, setOnlyReferee] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [invite, setInvite] = useState<InviteForm>({ full_name: "", phone: "", email: "", password: temporaryPassword() });
  const [teamReferees, setTeamReferees] = useState<AdminTeamMember[]>([]);
  const [bulkCourt, setBulkCourt] = useState("");
  const [bulkReferee, setBulkReferee] = useState("");
  const [bulkPeriod, setBulkPeriod] = useState<DayPeriod>("morning");
  const assignmentsRef = useRef<HTMLElement | null>(null);
  const isOrganizer = !!user && !user.managed_by_user_id;

  const load = useCallback(
    async (wantedDay: string | null) => {
      if (!token || Number.isNaN(tournamentId)) return;
      setLoading(true);
      try {
        const response = await api.referees.overview(token, tournamentId, wantedDay);
        setData(response.data);
        setDay(response.data.day);
        setError(null);
      } catch (err) {
        setError(errorText(err, "No se pudo cargar la pantalla de árbitros."));
      } finally {
        setLoading(false);
      }
    },
    [token, tournamentId],
  );

  const loadTeam = useCallback(async () => {
    if (!token || !isOrganizer) return;
    try {
      const response = await api.tournaments.listMyTeam(token);
      setTeamReferees(response.data.members.filter((member) => member.profile === "arbitro"));
    } catch {
      setTeamReferees([]);
    }
  }, [token, isOrganizer]);

  useEffect(() => {
    void load(null);
    void loadTeam();
  }, [load, loadTeam]);

  const referees = useMemo(() => sortRefereesAZ(data?.referees ?? []), [data]);
  const refereeIds = useMemo(() => new Set(referees.map((ref) => ref.user_id)), [referees]);
  const addableTeam = useMemo(
    () => sortRefereesAZ(teamReferees.filter((member) => !refereeIds.has(member.user_id))),
    [teamReferees, refereeIds],
  );
  const dayRows = data?.matches ?? [];
  const visibleRows = useMemo(
    () =>
      dayRows.filter(
        (row) =>
          (division === "all" || row.division_id === division) &&
          (onlyReferee === null || row.referee_user_id === onlyReferee),
      ),
    [dayRows, division, onlyReferee],
  );
  const blocks = useMemo(() => hourBlocks(visibleRows), [visibleRows]);
  const unassigned = dayRows.filter((row) => row.referee_user_id == null).length;
  const courts = useMemo(() => dayCourts(dayRows), [dayRows]);
  const dayDivisions = useMemo(() => {
    const ids = new Set(dayRows.map((row) => row.division_id));
    return (data?.divisions ?? []).filter((d) => ids.has(d.id));
  }, [data, dayRows]);
  const today = todayIso();

  async function assign(pairs: Array<{ match_id: number; referee_user_id: number | null }>, okText?: string) {
    if (!token || pairs.length === 0) {
      if (pairs.length === 0) setMessage("No hay partidos para asignar con esa selección.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.referees.assign(token, tournamentId, pairs);
      await load(day);
      setMessage(okText ?? null);
    } catch (err) {
      setError(errorText(err, "No se pudo guardar la asignación."));
    } finally {
      setBusy(false);
    }
  }

  async function savePublishMode(direct: boolean) {
    if (!token) return;
    setBusy(true);
    try {
      await api.referees.updateSettings(token, tournamentId, { referee_publishes_directly: direct });
      await load(day);
      setMessage(direct ? "Listo: el árbitro publica directo (W.O., RET y DEF siempre te llegan a revisar)." : "Listo: tú revisas lo que mande el árbitro.");
    } catch (err) {
      setError(errorText(err, "No se pudo guardar el ajuste."));
    } finally {
      setBusy(false);
    }
  }

  async function notifyReferees() {
    if (!token || !day) return;
    setBusy(true);
    setError(null);
    try {
      const response = await api.referees.notify(token, tournamentId, day);
      const s = response.data;
      setMessage(
        s.referees === 0
          ? "Nadie que avisar: ningún árbitro tiene partidos este día."
          : `Aviso enviado a ${s.referees} ${s.referees === 1 ? "árbitro" : "árbitros"} por la app${
              s.sent ? `; ${s.sent} por WhatsApp` : ""
            }${s.email_sent ? `; ${s.email_sent} por correo` : ""}.`,
      );
    } catch (err) {
      setError(errorText(err, "No se pudo avisar a los árbitros."));
    } finally {
      setBusy(false);
    }
  }

  async function sendInvite() {
    if (!token) return;
    if (!invite.full_name.trim() || !invite.email.trim()) {
      setError("Escribe el nombre y el correo del árbitro (con el correo entra a Torneos123).");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.tournaments.addMyTeamMember(token, {
        profile: "arbitro",
        new_user: {
          full_name: invite.full_name.trim(),
          email: invite.email.trim(),
          password: invite.password,
          phone: invite.phone.trim() || undefined,
        },
        scope: "selected",
        tournament_ids: [tournamentId],
      });
      setMessage(`${invite.full_name.trim()} ya es árbitro de este torneo. Le llegó un correo con su acceso.`);
      setInvite({ full_name: "", phone: "", email: "", password: temporaryPassword() });
      setInviteOpen(false);
      await Promise.all([load(day), loadTeam()]);
    } catch (err) {
      setError(errorText(err, "No se pudo invitar al árbitro."));
    } finally {
      setBusy(false);
    }
  }

  async function addTeamReferee(member: AdminTeamMember) {
    if (!token) return;
    setBusy(true);
    try {
      if (member.scope === "selected") {
        await api.tournaments.setMyTeamMemberTournaments(token, member.user_id, {
          scope: "selected",
          tournament_ids: [...new Set([...member.tournament_ids, tournamentId])],
        });
      } else {
        await api.tournaments.setMyTeamMemberTournaments(token, member.user_id, { scope: "all", tournament_ids: [] });
      }
      setMessage(`${member.full_name} ya es árbitro de este torneo.`);
      await Promise.all([load(day), loadTeam()]);
    } catch (err) {
      setError(errorText(err, "No se pudo agregar al árbitro."));
    } finally {
      setBusy(false);
    }
  }

  function copyAccess() {
    const text = `Torneos123 · árbitro\nEntra en https://torneos123.com/login\nCorreo: ${invite.email.trim()}\nContraseña temporal: ${invite.password}`;
    void navigator.clipboard?.writeText(text).then(
      () => setMessage("Acceso copiado: pégalo en WhatsApp al árbitro."),
      () => setMessage(null),
    );
  }

  function showMatchesOf(refereeId: number) {
    setOnlyReferee((current) => (current === refereeId ? null : refereeId));
    assignmentsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  function refereeSelect(row: RefereeMatchRow) {
    const none = row.referee_user_id == null;
    return (
      <select
        className={`t123-select referees-select${none ? " is-empty" : ""}`}
        aria-label={`Árbitro de ${row.player1_name} vs ${row.player2_name}`}
        value={row.referee_user_id ?? ""}
        disabled={busy}
        onChange={(event) =>
          void assign([{ match_id: row.id, referee_user_id: event.target.value ? Number(event.target.value) : null }])
        }
        data-referee-select={row.id}
      >
        <option value="">— Sin árbitro —</option>
        {referees.map((ref) => (
          <option key={ref.user_id} value={ref.user_id}>
            {ref.full_name}
          </option>
        ))}
      </select>
    );
  }

  if (Number.isNaN(tournamentId)) {
    return (
      <section className="card">
        <p className="form-error">ID de torneo inválido.</p>
      </section>
    );
  }

  // Árbitros apagado en Comunicación (paso 4, Eduardo 2026-10-17): aviso
  // amable en vez de 404; las asignaciones guardadas no se tocan.
  if (data && data.settings.referees_enabled === false) {
    return (
      <section className="card referees-off" data-referees-off>
        <h1 className="card__title">Árbitros está apagado en este torneo</h1>
        <p className="muted">
          En Comunicación elegiste no administrar árbitros dentro de la plataforma. Actívalo ahí para invitar
          árbitros y asignarles partidos.
        </p>
        <Link className="btn btn--primary" to={`/admin/tournaments/${tournamentId}/setup/settings`}>
          Activar en Comunicación ›
        </Link>
      </section>
    );
  }

  const publishesDirectly = data?.settings.referee_publishes_directly ?? false;
  const activeCount = referees.filter((ref) => !ref.invited_pending).length;

  return (
    <div className="referees-page">
      <section className="referees-hero">
        <div className="referees-hero__head">
          <span className="referees-hero__eyebrow">OPERACIÓN · {(data?.tournament.name ?? "Torneo").toUpperCase()}</span>
          <h1 className="referees-hero__title">Árbitros</h1>
          <span className="referees-hero__subtitle">Invita árbitros a este torneo y asígnalos a partidos.</span>
        </div>
        <div className="referees-kpis">
          <div className="referees-kpi referees-kpi--wide">
            <span>Árbitros</span>
            <strong>{referees.length}</strong>
          </div>
          <div className="referees-kpi referees-kpi--wide">
            <span>Activos</span>
            <strong>{activeCount}</strong>
          </div>
          <div className="referees-kpi">
            <span>{day === today ? "Partidos hoy" : "Partidos del día"}</span>
            <strong>{dayRows.length}</strong>
          </div>
          <div className="referees-kpi">
            <span>Sin árbitro</span>
            <strong>{unassigned}</strong>
          </div>
        </div>
      </section>

      {error ? (
        <p className="form-error" role="alert">
          {error}
        </p>
      ) : null}
      {message ? (
        <p className="form-message" role="status">
          {message}
        </p>
      ) : null}
      {data && data.pending_referee_reports > 0 ? (
        <p className="referees-pending">
          {data.pending_referee_reports === 1
            ? "1 resultado de árbitro espera tu revisión."
            : `${data.pending_referee_reports} resultados de árbitros esperan tu revisión.`}{" "}
          <Link to={routes.adminTournamentResultReports(tournamentId)}>Revisar en Resultados reportados</Link>
        </p>
      ) : null}

      <section className="referees-card" aria-labelledby="referees-list-title">
        <div className="referees-card__head">
          <div className="referees-step">
            <span className="referees-step__num">1</span>
            <strong id="referees-list-title">ÁRBITROS DE ESTE TORNEO</strong>
          </div>
          {isOrganizer ? (
            <button type="button" className="referees-btn referees-btn--lime" onClick={() => setInviteOpen((open) => !open)}>
              + Invitar árbitro
            </button>
          ) : null}
        </div>
        {loading && !data ? <p className="muted">Cargando…</p> : null}
        {data && referees.length === 0 ? (
          <p className="muted">Aún no hay árbitros en este torneo. Invita al primero.</p>
        ) : null}
        {referees.length > 0 ? (
          <div className="referees-list" role="table" aria-label="Árbitros de este torneo">
            <div className="referees-list__head" role="row">
              <span role="columnheader">NOMBRE</span>
              <span role="columnheader">CONTACTO</span>
              <span role="columnheader">ESTADO</span>
              <span role="columnheader">{day === today ? "HOY" : "ESTE DÍA"}</span>
              <span role="columnheader" aria-label="Acciones" />
            </div>
            {referees.map((ref) => (
              <div key={ref.user_id} className="referees-list__row" role="row" data-referee-row={ref.user_id}>
                <div className="referees-list__name" role="cell">
                  <span className="referees-avatar" aria-hidden="true">
                    {initialsOf(ref.full_name)}
                  </span>
                  <div className="referees-list__who">
                    <strong>{ref.full_name}</strong>
                    <span className="referees-list__contact-inline">{ref.phone || ref.email}</span>
                  </div>
                </div>
                <span className="referees-list__contact" role="cell">
                  {ref.phone || ref.email}
                </span>
                <span role="cell">
                  {ref.invited_pending ? (
                    <span className="referees-chip referees-chip--invited">Invitado · sin abrir</span>
                  ) : (
                    <span className="referees-chip referees-chip--active">Activo</span>
                  )}
                </span>
                <span className="referees-list__count" role="cell">
                  {ref.matches_on_day} {day === today ? "hoy" : "este día"}
                </span>
                <span className="referees-list__action" role="cell">
                  <button
                    type="button"
                    className={`referees-btn referees-btn--outline${onlyReferee === ref.user_id ? " is-active" : ""}`}
                    onClick={() => showMatchesOf(ref.user_id)}
                    aria-pressed={onlyReferee === ref.user_id}
                  >
                    {onlyReferee === ref.user_id ? "Ver todos" : "Ver partidos"}
                  </button>
                </span>
              </div>
            ))}
          </div>
        ) : null}
        {addableTeam.length > 0 ? (
          <div className="referees-team">
            <span className="muted">De tu Equipo (ya son árbitros, sin volver a invitarlos):</span>
            <div className="referees-team__list">
              {addableTeam.map((member) => (
                <button
                  key={member.user_id}
                  type="button"
                  className="referees-btn referees-btn--outline"
                  disabled={busy}
                  onClick={() => void addTeamReferee(member)}
                >
                  + {member.full_name}
                </button>
              ))}
            </div>
          </div>
        ) : null}
      </section>

      <div className={`referees-columns${inviteOpen ? "" : " is-single"}`}>
        {inviteOpen ? (
          <form
            className="referees-invite"
            onSubmit={(event) => {
              event.preventDefault();
              void sendInvite();
            }}
          >
            <strong>Invitar árbitro · {data?.tournament.name ?? ""}</strong>
            <div className="referees-invite__fields">
              <label>
                NOMBRE
                <input
                  value={invite.full_name}
                  onChange={(event) => setInvite({ ...invite, full_name: event.target.value })}
                  autoComplete="off"
                  required
                />
              </label>
              <label>
                CELULAR (WHATSAPP)
                <input
                  value={invite.phone}
                  inputMode="tel"
                  onChange={(event) => setInvite({ ...invite, phone: event.target.value })}
                  autoComplete="off"
                />
              </label>
              <label>
                CORREO (PARA ENTRAR)
                <input
                  type="email"
                  value={invite.email}
                  onChange={(event) => setInvite({ ...invite, email: event.target.value })}
                  autoComplete="off"
                  required
                />
              </label>
              <label>
                CONTRASEÑA TEMPORAL
                <input
                  value={invite.password}
                  minLength={6}
                  onChange={(event) => setInvite({ ...invite, password: event.target.value })}
                  autoComplete="new-password"
                  required
                />
              </label>
            </div>
            <span className="referees-invite__note">
              Le llega el mismo correo de bienvenida que a tu Equipo (capturista, coadministrador). Entra solo a ESTE
              torneo; en Equipo puedes darle más torneos sin volver a invitarlo.
            </span>
            <div className="referees-invite__copy">
              <span>torneos123.com/login · {invite.email.trim() || "correo"}</span>
              <button type="button" className="referees-btn referees-btn--outline" onClick={copyAccess} disabled={!invite.email.trim()}>
                Copiar
              </button>
            </div>
            <div className="referees-invite__actions">
              <button type="button" className="referees-btn referees-btn--ghost" onClick={() => setInviteOpen(false)}>
                Cancelar
              </button>
              <button type="submit" className="referees-btn referees-btn--navy" disabled={busy}>
                Enviar invitación
              </button>
            </div>
          </form>
        ) : null}

        <section className="referees-card" aria-labelledby="referees-publish-title">
          <div className="referees-step">
            <span className="referees-step__num">2</span>
            <strong id="referees-publish-title">¿QUIÉN PUBLICA EL RESULTADO?</strong>
          </div>
          <div role="radiogroup" aria-labelledby="referees-publish-title" className="referees-radios">
            <label className={`referees-radio${!publishesDirectly ? " is-checked" : ""}`}>
              <input
                type="radio"
                name="referee-publish"
                checked={!publishesDirectly}
                disabled={busy || !data}
                onChange={() => void savePublishMode(false)}
              />
              <span>
                <strong>El organizador revisa</strong>
                <span>El árbitro envía; tú lo publicas desde Resultados (igual que los reportes de jugadores).</span>
              </span>
            </label>
            <label className={`referees-radio${publishesDirectly ? " is-checked" : ""}`}>
              <input
                type="radio"
                name="referee-publish"
                checked={publishesDirectly}
                disabled={busy || !data}
                onChange={() => void savePublishMode(true)}
              />
              <span>
                <strong>El árbitro publica directo</strong>
                <span>Se publica al guardar; tú puedes corregir o despublicar. W.O., RET y DEF siempre te llegan a revisar.</span>
              </span>
            </label>
          </div>
          <div className="referees-lock">
            🔒 El árbitro solo ve y captura los partidos que le asignes. No ve inscripciones, pagos, draw ni otras categorías.
          </div>
        </section>
      </div>

      <section className="referees-card" aria-labelledby="referees-day-title" ref={assignmentsRef}>
        <div className="referees-card__head">
          <div className="referees-step">
            <span className="referees-step__num">3</span>
            <strong id="referees-day-title">ASIGNACIONES DEL DÍA</strong>
          </div>
          {unassigned > 0 ? <span className="referees-chip referees-chip--amber">{unassigned} sin árbitro</span> : null}
        </div>
        {data && data.days.length === 0 ? (
          <p className="muted">Todavía no hay partidos con fecha. Prográmalos en Calendario y regresa a asignar árbitros.</p>
        ) : null}
        {data && data.days.length > 0 ? (
          <>
            <div className="referees-toolbar">
              <div className="referees-days" role="tablist" aria-label="Días del torneo">
                {data.days.map((item) => (
                  <button
                    key={item.date}
                    type="button"
                    role="tab"
                    aria-selected={item.date === day}
                    className={`referees-day${item.date === day ? " is-current" : ""}`}
                    onClick={() => {
                      setOnlyReferee(null);
                      void load(item.date);
                    }}
                  >
                    <strong>{dayTabLabel(item.date, item.date === today)}</strong>
                    <span>
                      {item.matches} part.{item.unassigned ? ` · ${item.unassigned} sin árbitro` : ""}
                    </span>
                  </button>
                ))}
              </div>
              {dayDivisions.length > 1 ? (
                <div className="referees-filters" aria-label="Categoría">
                  <button
                    type="button"
                    className={`referees-filter${division === "all" ? " is-current" : ""}`}
                    onClick={() => setDivision("all")}
                  >
                    Todas
                  </button>
                  {dayDivisions.map((d) => (
                    <button
                      key={d.id}
                      type="button"
                      className={`referees-filter${division === d.id ? " is-current" : ""}`}
                      onClick={() => setDivision(d.id)}
                    >
                      {d.code?.trim() || d.name}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>

            {courts.length > 0 && referees.length > 0 ? (
              <div className="referees-bulk">
                <span>Por cancha:</span>
                <select className="t123-select" aria-label="Cancha" value={bulkCourt} onChange={(event) => setBulkCourt(event.target.value)}>
                  <option value="">Cancha…</option>
                  {courts.map((court) => (
                    <option key={court} value={court}>
                      Cancha {court}
                    </option>
                  ))}
                </select>
                <span aria-hidden="true">→</span>
                <select className="t123-select" aria-label="Árbitro para la cancha" value={bulkReferee} onChange={(event) => setBulkReferee(event.target.value)}>
                  <option value="">Árbitro…</option>
                  {referees.map((ref) => (
                    <option key={ref.user_id} value={ref.user_id}>
                      {ref.full_name}
                    </option>
                  ))}
                </select>
                <select className="t123-select" aria-label="Parte del día" value={bulkPeriod} onChange={(event) => setBulkPeriod(event.target.value as DayPeriod)}>
                  <option value="morning">a la mañana</option>
                  <option value="afternoon">a la tarde</option>
                  <option value="all">a todo el día</option>
                </select>
                <button
                  type="button"
                  className="referees-btn referees-btn--navy"
                  disabled={busy || !bulkCourt || !bulkReferee}
                  onClick={() =>
                    void assign(
                      courtAssignments(dayRows, bulkCourt, Number(bulkReferee), bulkPeriod),
                      `Cancha ${bulkCourt} asignada.`,
                    )
                  }
                >
                  Aplicar
                </button>
              </div>
            ) : null}

            {onlyReferee !== null ? (
              <p className="referees-filtering">
                Solo los partidos de <strong>{referees.find((r) => r.user_id === onlyReferee)?.full_name}</strong> ·{" "}
                <button type="button" className="link-button" onClick={() => setOnlyReferee(null)}>
                  ver todos
                </button>
              </p>
            ) : null}

            <div className="referees-table" role="table" aria-label="Asignaciones del día">
              <div className="referees-table__head" role="row">
                <span role="columnheader">HORA</span>
                <span role="columnheader">CANCHA</span>
                <span role="columnheader">GRUPO</span>
                <span role="columnheader">JUGADOR 1</span>
                <span role="columnheader">JUGADOR 2</span>
                <span role="columnheader">ÁRBITRO (A→Z)</span>
              </div>
              {blocks.map((block) => (
                <div key={block.time || "sin-hora"} className="referees-block" role="rowgroup">
                  <div className="referees-block__head" role="row">
                    <strong>{block.time || "Sin hora"}</strong>
                    <span className="referees-block__line" aria-hidden="true" />
                    {referees.length > 0 && block.rows.some((row) => row.referee_user_id == null) ? (
                      <select
                        className="t123-select referees-block__assign"
                        aria-label={`Asignar bloque de las ${block.time || "sin hora"}`}
                        value=""
                        disabled={busy}
                        onChange={(event) => {
                          if (!event.target.value) return;
                          void assign(
                            blockAssignments(block, Number(event.target.value)),
                            `Bloque de las ${block.time} asignado.`,
                          );
                        }}
                      >
                        <option value="">Asignar bloque ▾</option>
                        {referees.map((ref) => (
                          <option key={ref.user_id} value={ref.user_id}>
                            {ref.full_name}
                          </option>
                        ))}
                      </select>
                    ) : null}
                  </div>
                  {block.rows.map((row) => (
                    <div
                      key={row.id}
                      className={`referees-row${row.referee_user_id == null ? " is-unassigned" : ""}`}
                      role="row"
                      data-assignment-row={row.id}
                    >
                      <span className="referees-row__time" role="cell">
                        {row.scheduled_time?.slice(0, 5) || "—"}
                      </span>
                      <strong className="referees-row__court" role="cell">
                        {row.court ? `C${row.court}` : "—"}
                      </strong>
                      <span role="cell" className="referees-row__chip-cell">
                        <span
                          className="referees-group-chip"
                          title={[row.division_name, row.group_name].filter(Boolean).join(" · ") || matchChipLabel(row)}
                          style={{ borderColor: matchChipColor(row), color: matchChipColor(row) }}
                        >
                          {matchChipLabel(row)}
                        </span>
                      </span>
                      <strong className="referees-row__p1" role="cell">
                        {row.player1_name}
                      </strong>
                      <span className="referees-row__vs" aria-hidden="true">
                        vs
                      </span>
                      <strong className="referees-row__p2" role="cell">
                        {row.player2_name}
                      </strong>
                      <span className="referees-row__select" role="cell">
                        {refereeSelect(row)}
                      </span>
                    </div>
                  ))}
                </div>
              ))}
              {blocks.length === 0 ? <p className="muted">No hay partidos con este filtro.</p> : null}
            </div>
            <div className="referees-notify">
              <button
                type="button"
                className="referees-btn referees-btn--navy"
                disabled={busy || !day || referees.length === 0}
                onClick={() => void notifyReferees()}
              >
                Avisar a los árbitros
              </button>
              <span className="muted">Cada árbitro recibe su lista del día.</span>
            </div>
          </>
        ) : null}
      </section>

      <section className="referees-card" aria-labelledby="referees-calendar-title">
        <div className="referees-card__head">
          <div className="referees-step">
            <span className="referees-step__num">4</span>
            <strong id="referees-calendar-title">DESDE CALENDARIO (VISTA DÍA)</strong>
          </div>
          <Link className="referees-btn referees-btn--outline" to={routes.adminTournamentSchedule(tournamentId)}>
            Abrir Calendario
          </Link>
        </div>
        <span className="muted">
          Cada tarjeta del día muestra al árbitro y en su panel lo cambias con «Árbitro ▾». Ahí mismo están «🖨 Rol de juego
          del día» y «🖨 Hoja de control».
        </span>
      </section>
      <TournamentBottomNav tournamentId={tournamentId} />
    </div>
  );
}
